import { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import clsx from 'clsx';

/**
 * Internal properties for the Container component.
 */
type ContainerProps<T extends ElementType> = {
  /** The HTML element to render as. */
  as?: T;
  /** Optional CSS class name for the outer container. */
  className?: string;
  /** Optional CSS class name for the inner container. */
  classNameChildren?: string;
  /** The content to be rendered inside the container. */
  children: ReactNode;
};

/**
 * Combined properties for the Container component.
 */
type TContainerProps<T extends ElementType = 'div'> = Omit<
  ComponentPropsWithoutRef<T>,
  keyof ContainerProps<T>
> &
  ContainerProps<T>;

/**
 * A layout component that centers and constrains its content within a maximum width.
 */
export function Container<T extends ElementType = 'div'>({
  as,
  className,
  classNameChildren,
  children,
}: TContainerProps<T>) {
  const Component = as ?? 'div';

  return (
    <Component className={clsx('mx-auto max-w-7xl px-6 lg:px-8', className)}>
      <div
        className={clsx('mx-auto max-w-2xl lg:max-w-none', classNameChildren)}
      >
        {children}
      </div>
    </Component>
  );
}
